// @flow

import * as React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {actionsRolesAllow} from './ActionsRolesAllow';
import type {RoleDescriptionProps} from './RoleDescription';

const adminRoleMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.adminRole.roleName',
    description: 'The name of the admin role.',
    defaultMessage: 'Administrator',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.adminRole.shortDescription',
    description: 'The short description of the admin role.',
    defaultMessage:
      'Can act as the administrator, transfer money, and edit settings',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.adminRole.descriptionOfWhatRoleCanDo',
    description: 'The description of what the admin role can do.',
    defaultMessage:
      'This role is for anyone who needs similar access as the account owner—they can see and manage almost everything.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.adminRole.descriptionOfWhatRoleCannotDo',
    description: "The description of what the admin role can't do.",
    defaultMessage:
      "They can't change the default currency, delete the default bank account, or change the account owner.",
  },
});

const adminRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...adminRoleMessages.roleName} />,
  roleId: 'admin',
  isBeta: false,
  shortDescription: (
    <FormattedMessage {...adminRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...adminRoleMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage {...adminRoleMessages.descriptionOfWhatRoleCannotDo} />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.editAllProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.changeDefaultPayoutCurrency} />,
    <FormattedMessage {...actionsRolesAllow.deleteTheDefaultBankAccount} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
  ],
};

const iamAdminRoleMessages = defineMessages({
  roleName: {
    id: 'docsApp.components.contrib.roleDescriptions.iamAdminRole.roleName',
    description: 'The name of the iam admin role.',
    defaultMessage: 'IAM Admin',
  },
  shortDescription: {
    id:
      'docsApp.components.contrib.roleDescriptions.iamAdminRole.shortDescription',
    description: 'The short description of the iam admin role.',
    defaultMessage: 'Can invite team members and assign roles',
  },
  descriptionOfWhatRoleCanDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.iamAdminRole.descriptionOfWhatRoleCanDo',
    description: 'The description of what the iam admin role can do.',
    defaultMessage:
      'The Identity and Access Management (IAM) Admin role is for people who need to invite team members and assign roles.',
  },
  descriptionOfWhatRoleCannotDo: {
    id:
      'docsApp.components.contrib.roleDescriptions.iamAdminRole.descriptionOfWhatRoleCannotDo',
    description: "The description of what the iam admin role can't do.",
    defaultMessage: "They can't do anything beyond access management.",
  },
});

const iamAdminRoleDescriptionProps: RoleDescriptionProps = {
  roleName: <FormattedMessage {...iamAdminRoleMessages.roleName} />,
  roleId: 'iam_admin',
  isBeta: true,
  shortDescription: (
    <FormattedMessage {...iamAdminRoleMessages.shortDescription} />
  ),
  descriptionOfWhatRoleCanDo: (
    <FormattedMessage {...iamAdminRoleMessages.descriptionOfWhatRoleCanDo} />
  ),
  descriptionOfWhatRoleCannotDo: (
    <FormattedMessage {...iamAdminRoleMessages.descriptionOfWhatRoleCannotDo} />
  ),
  someThingsThisRoleCanDo: [
    <FormattedMessage {...actionsRolesAllow.addEditRemoveTeamMembers} />,
    <FormattedMessage {...actionsRolesAllow.createAndManageGroups} />,
    <FormattedMessage {...actionsRolesAllow.viewSecurityHistory} />,
  ],
  someThingsThisRoleCannotDo: [
    <FormattedMessage {...actionsRolesAllow.viewAndResolveDisputes} />,
    <FormattedMessage {...actionsRolesAllow.createViewRefundPayments} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteProducts} />,
    <FormattedMessage {...actionsRolesAllow.createViewEditDeleteCustomers} />,
    <FormattedMessage
      {...actionsRolesAllow.createViewEditRejectConnectedAccounts}
    />,
    <FormattedMessage {...actionsRolesAllow.editConnectedAcctCapabilities} />,
    <FormattedMessage
      {...actionsRolesAllow.editConnectedAccountPayoutSchedule}
    />,
    <FormattedMessage {...actionsRolesAllow.viewRadar} />,
    <FormattedMessage {...actionsRolesAllow.viewBalance} />,
    <FormattedMessage {...actionsRolesAllow.viewTransfers} />,
    <FormattedMessage
      {...actionsRolesAllow.transferBalanceAndReverseTransfers}
    />,
    <FormattedMessage
      {...actionsRolesAllow.payoutBalanceToExternalBankAccount}
    />,
    <FormattedMessage {...actionsRolesAllow.manageFinancialReports} />,
    <FormattedMessage {...actionsRolesAllow.bulkExport} />,
    <FormattedMessage {...actionsRolesAllow.createEditDeleteAPIKeys} />,
    <FormattedMessage {...actionsRolesAllow.addAndEditBankAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.editPayoutSchedule} />,
    <FormattedMessage {...actionsRolesAllow.editAccountDetails} />,
    <FormattedMessage {...actionsRolesAllow.changeAccountOwner} />,
    <FormattedMessage {...actionsRolesAllow.editAllProductSettings} />,
    <FormattedMessage
      {...actionsRolesAllow.connectAccountToConnectPlatforms}
      values={{
        a: (content) => <a href="https://stripe.com/docs/connect">{content}</a>,
      }}
    />,
    <FormattedMessage {...actionsRolesAllow.viewEventsAndLogs} />,
    <FormattedMessage {...actionsRolesAllow.viewPayouts} />,
  ],
};

export const allAdminRolesProps = [
  adminRoleDescriptionProps,
  iamAdminRoleDescriptionProps,
];
